import * as THREE from "three";
import type { PooledVfx } from "../vfxPool";

/**
 * (C) A bark made visible (petPresent.ts): on each of the clip's two barks a shout of gold rings leaves its mouth and
 * steps off toward the gate (where the troop it called musters), and on the first a gold mote flies from its mouth to
 * that troop and lands in a few sparkles — the pup DID the muster. All on the shared sprite VFX (art/vfx.js), pooled.
 */
export const PET_BARK = {
  colour: [1, 0.84, 0.36] as [number, number, number],
  /**
   * The shout: small rings (sizes at scale 1, world units) from ahead of and above its mouth (`lead`, `rise`: the face
   * and body stay clear — a ring's glow over the pup washed it out), stepping this far toward the gate each, this long
   * apart (s), this bright.
   */
  rings: [0.2, 0.16] as const, lead: 0.2, rise: 0.12, ringStep: 0.16, ringGap: 0.07, ringLife: 0.3, bright: 1.2,
  /** The second bark's shout is this share of the first's. */
  second: 0.75,
  /** The mote: its size, flight (s) and arc (world units); the sparkles where it lands. */
  mote: 0.15, moteLife: 0.42, moteArc: 0.45, landSparkles: 8, landRadius: 0.18,
} as const;

const _dir = new THREE.Vector3();

export class PetBark {
  constructor(private readonly fx: () => PooledVfx | null, private readonly scale: number) {}

  /**
   * One bark from `mouth` (world): the shout toward `toward` (the gate, world x / z), and on the first bark (`first`) the mote to `troop` (where that troop stands now: null when none).
   */
  bark(mouth: THREE.Vector3, toward: THREE.Vector3, first: boolean, troop: THREE.Vector3 | null): void {
    const fx = this.fx();
    if (!fx) return;
    const k = this.scale * (first ? 1 : PET_BARK.second);
    _dir.set(toward.x - mouth.x, 0, toward.z - mouth.z);
    if (_dir.lengthSq() < 1e-6) _dir.set(0, 0, 1);
    _dir.normalize();
    PET_BARK.rings.forEach((size, i) => {
      const at = mouth.clone().addScaledVector(_dir, (PET_BARK.lead + PET_BARK.ringStep * i) * this.scale);
      at.y += PET_BARK.rise * this.scale;
      const go = () => fx.ring(at, { size: size * k, life: PET_BARK.ringLife, color: PET_BARK.colour, flat: false, bright: PET_BARK.bright });
      if (i === 0) go(); else fx.later(PET_BARK.ringGap * i, go);
    });
    if (!first || !troop) return;
    const from = mouth.clone();
    fx.projectile({
      from, to: troop.clone(), life: PET_BARK.moteLife, frame: "orb", size: PET_BARK.mote * this.scale, color: PET_BARK.colour, arc: PET_BARK.moteArc, trail: true,
      onHit: (at) => fx.sparkles(at, PET_BARK.landSparkles, { color: PET_BARK.colour, radius: PET_BARK.landRadius, rise: 0.8, life: 0.6, size: 0.12 }),
    });
  }
}
